import type {DataTableFilterMeta, DataTableOperatorFilterMetaData, SortOrder} from "primereact/datatable";

import type {GeoRefAddress} from "@reva/shared/geo-reference";

import type {RevaSortOrder} from "../api/payload-types/search-valuation";

/**
 * Convert sort order from PrimeReact DataTable to Reva REST SortOrder format.
 */
export function dataTableSortOrderToString(sortOrderNumber: SortOrder): RevaSortOrder {
    if (!sortOrderNumber) return null;
    if (sortOrderNumber > 0) return "ASC";
    return "DESC";
}

/**
 * Convert sort order from Reva REST SortOrder to PrimeReact DataTable format.
 */
export function dataTableSortOrderFromString(sortOrderString: RevaSortOrder): SortOrder {
    if (!sortOrderString) return null;
    switch (sortOrderString) {
        case "ASC":
            return 1;
        case "DESC":
            return -1;
        default:
            return 0;
    }
}

/**
 * Create DataTable filter meta
 * @param value value of the filter, in the type matching the datatype of the field
 */
export function createFilterMeta(value: unknown): DataTableOperatorFilterMetaData {
    return {
        operator: "and",
        constraints: [
            {
                matchMode: "custom",
                value: value ?? null,
            },
        ],
    };
}

/**
 * Extract filter value from DataTable filter meta
 * @return filtered value
 */
export function extractFilterMeta(filters: DataTableFilterMeta | undefined, field: string): unknown {
    const meta = filters?.[field];
    if (meta && "constraints" in meta) {
        return meta.constraints?.[0]?.value ?? null;
    }
    return null;
}

/**
 * Build a stable React list key for a geo-referenced address.
 *
 * Combines the German display name with the exact coordinates. The tuple
 * (displayNameDE, lat, lon) is expected to be distinct for every candidate of an
 * address disambiguation, so the key stays bound to its data record across
 * re-renders — unlike an array index, which is tied to the position instead.
 * @param address the geo-referenced address
 * @return a stable, unique key string
 */
export function georefAddressStableKey(address: GeoRefAddress): string {
    return `${address.displayNameDE}|${address.lat}|${address.lon}`;
}
